import { useEffect, useMemo, useState, type FormEvent } from "react";
import {
  DEFAULT_BACKTEST_STRATEGY_V2,
  type BacktestDecisionDiagnostic,
  type BacktestResult,
  type BacktestTrade,
  type MarketDataCoverageResponse,
  type StrategyDefinitionV2,
  type StrategyGenerationUsage,
  type StrategyMarketFamily,
  type StrategySignalV2,
  type StrategyUnderlying,
  type StrategyWeightedSignalV2,
} from "@papertiger/contracts";
import { api } from "./api.js";
import {
  calculateBacktestAvailability,
  type BacktestAvailabilityWindow,
} from "./backtest-availability.js";

/** Keeps the preserved Backtests surface visible while historical collection is paused. */
const BACKTESTING_IS_IN_DEVELOPMENT = true;

/** Supported form underlyings in display order. */
const UNDERLYINGS: readonly StrategyUnderlying[] = [
  "BTC",
  "ETH",
  "SOL",
  "XRP",
  "DOGE",
  "BNB",
];

/** Signals exposed by the version-two form. */
const SIGNAL_TYPES: readonly StrategySignalV2["type"][] = [
  "depth_imbalance",
  "order_flow_imbalance",
  "aggressive_trade_imbalance",
  "microprice_deviation",
  "liquidity_persistence",
  "book_resilience",
  "ephemeral_liquidity_score",
  "reference_momentum",
  "reference_target_distance",
  "price",
  "trend",
  "book_imbalance",
  "reference_direction",
];

/** One ready-to-edit, schema-valid strategy supplied by the Backtests page. */
export interface BacktestTemplate {
  /** Stable selection value. */
  id: string;
  /** Short label shown in the template selector. */
  label: string;
  /** Plain-language hypothesis used by the optional AI builder. */
  idea: string;
  /** Complete V2 form state to apply without an AI request. */
  strategy: StrategyDefinitionV2;
}

/** Input fields that vary between deliberately executable sample templates. */
interface ExecutableBacktestTemplateConfiguration {
  /** Stable template selection value. */
  id: string;
  /** Label visible in the form selector. */
  label: string;
  /** Strategy name. */
  name: string;
  /** Relative entry-window anchor. */
  anchor: StrategyDefinitionV2["entry"]["anchor"];
  /** Inclusive window start relative to the anchor. */
  startOffsetSeconds: number;
  /** Inclusive window end relative to the anchor. */
  endOffsetSeconds: number;
  /** Outcome chosen for the entry. */
  outcomeSelection: StrategyDefinitionV2["entry"]["outcomeSelection"];
  /** Causal signal to sample. */
  signal: StrategySignalV2;
  /** Score threshold selected for the signal. */
  minimumScorePercent100: number;
}

/**
 * Builds a minimal executable sample rule for validating stored BTC L2 replay.
 *
 * @param configuration Fields that distinguish one sample hypothesis.
 * @returns A complete V2 strategy definition with deliberately permissive gates.
 */
function createExecutableBacktestTemplate(
  configuration: ExecutableBacktestTemplateConfiguration,
): BacktestTemplate {
  const disclosure =
    "Exploratory executable trade sample only; it disables the modeled-edge gate and makes no alpha or profitability claim.";
  return {
    id: configuration.id,
    label: configuration.label,
    idea: `${configuration.name}: ${disclosure}`,
    strategy: {
      schemaVersion: 2,
      name: configuration.name,
      description: disclosure,
      universe: {
        families: [
          { venue: "polymarket", underlying: "BTC", durationMinutes: 5 },
        ],
      },
      entry: {
        anchor: configuration.anchor,
        startOffsetSeconds: configuration.startOffsetSeconds,
        endOffsetSeconds: configuration.endOffsetSeconds,
        outcomeSelection: configuration.outcomeSelection,
        signals: [
          { id: "sample-signal", weight: 1, signal: configuration.signal },
        ],
        minimumScorePercent100: configuration.minimumScorePercent100,
        minimumScorePersistenceMilliseconds: 0,
        referenceAgreement: null,
        maximumBookAgeMilliseconds: null,
        maximumEphemeralLiquidityPenaltyPercent100: null,
        expectedMoveAtFullScorePercent1: 0,
        minimumExpectedNetEdgePercent100: -100,
        order: {
          type: "market",
          limitPricePercent1: null,
          sizing: "fixed_dollars",
          size: 1,
        },
        maxEntriesPerMarket: 1,
      },
      execution: {
        latencyMilliseconds: 0,
        displayedDepthParticipationPercent100: 100,
        maximumSlippagePercent100: null,
        expectedEdgeSlippageBufferPercent100: 0,
      },
      exit: {
        takeProfitPercent100: null,
        stopLossPercent100: null,
        maximumHoldingSeconds: 30,
        signalReversalScorePercent100: null,
        pressureDecayScorePercent100: null,
        exitBeforeCloseSeconds: 1,
      },
      portfolio: { maxConcurrentPositions: 1, maxPositionDollars: 5 },
    },
  };
}

/**
 * Deliberately simple BTC five-minute templates for producing executable replay
 * samples. They are experiments, not signals of an expected trading edge.
 */
export const BACKTEST_TEMPLATES: readonly BacktestTemplate[] = [
  createExecutableBacktestTemplate({
    id: "btc-late-leader-sample",
    label: "BTC · late leader sample",
    name: "BTC 5m late leader sample",
    anchor: "market_close",
    startOffsetSeconds: -60,
    endOffsetSeconds: -5,
    outcomeSelection: "leading",
    signal: { type: "price", field: "ask", operator: "gte", valuePercent1: 0 },
    minimumScorePercent100: 50,
  }),
  createExecutableBacktestTemplate({
    id: "btc-late-up-sample",
    label: "BTC · late Up sample",
    name: "BTC 5m late Up sample",
    anchor: "market_close",
    startOffsetSeconds: -60,
    endOffsetSeconds: -5,
    outcomeSelection: "up",
    signal: { type: "price", field: "ask", operator: "gte", valuePercent1: 0 },
    minimumScorePercent100: 50,
  }),
  createExecutableBacktestTemplate({
    id: "btc-late-down-sample",
    label: "BTC · late Down sample",
    name: "BTC 5m late Down sample",
    anchor: "market_close",
    startOffsetSeconds: -60,
    endOffsetSeconds: -5,
    outcomeSelection: "down",
    signal: { type: "price", field: "ask", operator: "gte", valuePercent1: 0 },
    minimumScorePercent100: 50,
  }),
  createExecutableBacktestTemplate({
    id: "btc-late-depth-sample",
    label: "BTC · depth imbalance sample",
    name: "BTC 5m depth imbalance sample",
    anchor: "market_close",
    startOffsetSeconds: -60,
    endOffsetSeconds: -5,
    outcomeSelection: "leading",
    signal: { type: "depth_imbalance", depth: { mode: "levels", levels: 1 } },
    minimumScorePercent100: -100,
  }),
  createExecutableBacktestTemplate({
    id: "btc-late-order-flow-sample",
    label: "BTC · order-flow sample",
    name: "BTC 5m order-flow sample",
    anchor: "market_close",
    startOffsetSeconds: -60,
    endOffsetSeconds: -5,
    outcomeSelection: "leading",
    signal: {
      type: "order_flow_imbalance",
      lookbackMilliseconds: 1_000,
      depth: { mode: "levels", levels: 1 },
    },
    minimumScorePercent100: -100,
  }),
  createExecutableBacktestTemplate({
    id: "btc-late-microprice-sample",
    label: "BTC · microprice sample",
    name: "BTC 5m microprice sample",
    anchor: "market_close",
    startOffsetSeconds: -60,
    endOffsetSeconds: -5,
    outcomeSelection: "leading",
    signal: { type: "microprice_deviation" },
    minimumScorePercent100: -100,
  }),
];

/** Sortable completed-trade fields. */
type TradeSortKey =
  | "entryAt"
  | "marketTitle"
  | "outcomeName"
  | "entryPricePercent1"
  | "exitPricePercent1"
  | "quantity"
  | "profitDollars"
  | "returnPercent100"
  | "exitReason";

/** Current trade-table ordering. */
interface TradeSort {
  /** Selected field. */
  key: TradeSortKey;
  /** Sort direction. */
  direction: "asc" | "desc";
}

/** Shared strategy editor properties. */
interface StrategyEditorProps {
  /** Current definition. */
  strategy: StrategyDefinitionV2;
  /** Replaces the full definition. */
  onChange: (strategy: StrategyDefinitionV2) => void;
}

/**
 * Responsive version-two strategy form, AI builder, and L2 backtest results.
 *
 * @returns Complete authenticated backtesting surface.
 */
export function Backtester(props: {
  /** Effective access tier for the signed-in account. */
  accessTier: "free" | "trial" | "paid";
}): React.JSX.Element {
  const defaultEnd = new Date();
  const defaultStart = new Date(defaultEnd.getTime() - 24 * 60 * 60 * 1_000);
  const [strategy, setStrategy] =
    useState<StrategyDefinitionV2>(BACKTEST_TEMPLATES[0]!.strategy);
  const [selectedTemplateId, setSelectedTemplateId] = useState(
    BACKTEST_TEMPLATES[0]!.id,
  );
  const [idea, setIdea] = useState(BACKTEST_TEMPLATES[0]!.idea);
  const [usage, setUsage] = useState<StrategyGenerationUsage | null>(null);
  const [start, setStart] = useState(toLocalDateTime(defaultStart));
  const [end, setEnd] = useState(toLocalDateTime(defaultEnd));
  const [initialCapitalDollars, setInitialCapitalDollars] = useState(1_000);
  const [generating, setGenerating] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<BacktestResult | null>(null);
  const [coverage, setCoverage] =
    useState<MarketDataCoverageResponse | null>(null);
  const [coverageError, setCoverageError] = useState<string | null>(null);
  const availabilityWindow = useMemo(
    () =>
      coverage === null
        ? null
        : calculateBacktestAvailability(
            coverage.coverage,
            strategy.universe.families,
            coverage.collectionMode ?? "continuous",
          ),
    [coverage, strategy.universe.families],
  );

  useEffect(() => {
    if (BACKTESTING_IS_IN_DEVELOPMENT) return;
    let active = true;
    void api
      .getMarketDataCoverage()
      .then((response) => {
        if (!active) return;
        setCoverage(response);
        setCoverageError(null);
      })
      .catch((reason: unknown) => {
        if (!active) return;
        setCoverageError(readError(reason));
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (availabilityWindow === null) return;
    setStart(toLocalDateTime(new Date(availabilityWindow.defaultStart)));
    setEnd(toLocalDateTime(new Date(availabilityWindow.defaultEnd)));
  }, [availabilityWindow]);

  /**
   * Populates the editable form with one schema-constrained Luna response.
   *
   * @returns Nothing.
   */
  async function generateStrategy(): Promise<void> {
    setGenerating(true);
    setError(null);
    try {
      const generated = await api.generateStrategy(idea);
      setStrategy(generated.strategy);
      setUsage(generated.usage);
      setSelectedTemplateId("");
    } catch (reason) {
      setError(readError(reason));
    } finally {
      setGenerating(false);
    }
  }

  /**
   * Runs and displays one persisted historical simulation.
   *
   * @param event Form submission.
   * @returns Nothing.
   */
  async function runBacktest(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (availabilityWindow === null) {
      setError("No selectable replayable L2 range is available.");
      return;
    }
    const startIso = new Date(start).toISOString();
    const endIso = new Date(end).toISOString();
    if (
      Date.parse(startIso) < Date.parse(availabilityWindow.availableStart) ||
      Date.parse(endIso) > Date.parse(availabilityWindow.availableEnd)
    ) {
      setError("Choose a simulation window inside the stored L2 range.");
      return;
    }
    setRunning(true);
    setError(null);
    setResult(null);
    try {
      setResult(
        await api.runBacktest({
          strategy,
          start: startIso,
          end: endIso,
          initialCapitalDollars,
        }),
      );
    } catch (reason) {
      setError(readError(reason));
    } finally {
      setRunning(false);
    }
  }

  /**
   * Loads a ready-to-edit strategy and its plain-language explanation.
   *
   * @param templateId Selected template identifier.
   * @returns Nothing.
   */
  function applyTemplate(templateId: string): void {
    const template = BACKTEST_TEMPLATES.find(
      (candidate) => candidate.id === templateId,
    );
    if (template === undefined) return;
    setSelectedTemplateId(template.id);
    setStrategy(template.strategy);
    setIdea(template.idea);
    setUsage(null);
    setError(null);
    setResult(null);
  }

  return (
    <section className="backtester">
      <div className="backtester-hero">
        <div>
          <p className="eyebrow">Historical simulation</p>
          <h1>Backtest a strategy</h1>
          <p>
            Combine causal L2 signals, model delayed executable fills, and
            inspect every accepted or rejected decision.
          </p>
        </div>
        <span className="backtester-fidelity">
          Point-in-time · replayable L2
        </span>
      </div>

      <div
        className="notice backtesting-development-notice"
        id="backtesting-development-notice"
        role="status"
      >
        <strong>Backtesting with full market data is in development.</strong>{" "}
        {props.accessTier === "free"
          ? "Backtests require Trial or Paid access, and historical collection is also currently paused, so this form is available to review but cannot run simulations."
          : "Historical collection is currently paused, so this form is available to review but cannot run simulations."}
      </div>

      <fieldset
        aria-describedby="backtesting-development-notice"
        className="backtesting-disabled"
        disabled={BACKTESTING_IS_IN_DEVELOPMENT}
      >
      <section className="surface ai-strategy-builder">
        <div className="ai-strategy-heading">
          <div>
            <span className="eyebrow">AI form builder</span>
            <h2>Describe the trade you want to test</h2>
          </div>
          {usage !== null && <UsageSummary usage={usage} />}
        </div>
        <label className="backtest-template-selector">
          Starting template
          <select
            aria-label="Backtest strategy template"
            value={selectedTemplateId}
            onChange={(event) => applyTemplate(event.target.value)}
          >
            <option value="" disabled>
              Custom strategy
            </option>
            {BACKTEST_TEMPLATES.map((template) => (
              <option key={template.id} value={template.id}>
                {template.label}
              </option>
            ))}
          </select>
        </label>
        <textarea
          aria-label="Strategy idea"
          rows={4}
          value={idea}
          onChange={(event) => setIdea(event.target.value)}
          placeholder="For example: buy BTC 5-minute Up when one-second order flow and five-level depth agree…"
        />
        <div className="ai-strategy-actions">
          <button
            className="primary"
            disabled={generating || idea.trim().length < 3}
            onClick={() => void generateStrategy()}
            type="button"
          >
            {generating ? "Building strategy…" : "Populate form with Luna"}
          </button>
          <span>
            Luna returns a validated V2 definition and usage. Review its
            assumptions before running.
          </span>
        </div>
      </section>

      {error !== null && <div className="notice error">{error}</div>}

      <form className="backtest-layout" onSubmit={(event) => void runBacktest(event)}>
        <div className="backtest-form-stack">
          <StrategyEditor strategy={strategy} onChange={setStrategy} />
          <UniverseEditor strategy={strategy} onChange={setStrategy} />
          <TimingEditor strategy={strategy} onChange={setStrategy} />
          <SignalsEditor strategy={strategy} onChange={setStrategy} />
          <ExecutionEditor strategy={strategy} onChange={setStrategy} />
          <ExitEditor strategy={strategy} onChange={setStrategy} />
        </div>
        <aside className="surface backtest-run-card">
          <div>
            <p className="eyebrow">Run configuration</p>
            <h2>Simulation window</h2>
          </div>
          {BACKTESTING_IS_IN_DEVELOPMENT ? (
            <div className="backtest-coverage unavailable">
              <strong>Historical collection paused</strong>
              <span>Coverage will return when sampled collection is redeployed.</span>
            </div>
          ) : (
            <BacktestCoverageSummary
              coverageLoading={coverage === null && coverageError === null}
              error={coverageError}
              window={availabilityWindow}
              collectionMode={coverage?.collectionMode ?? "continuous"}
              scheduledNextCaptureAt={
                coverage?.scheduledNextCaptureAt ?? null
              }
            />
          )}
          <label>
            Start
            <input
              type="datetime-local"
              value={start}
              min={
                availabilityWindow === null
                  ? undefined
                  : toLocalDateTime(
                      new Date(availabilityWindow.availableStart),
                    )
              }
              max={
                availabilityWindow === null
                  ? undefined
                  : toLocalDateTime(new Date(availabilityWindow.availableEnd))
              }
              onChange={(event) => setStart(event.target.value)}
              disabled={availabilityWindow === null}
              required
            />
          </label>
          <label>
            End
            <input
              type="datetime-local"
              value={end}
              min={
                availabilityWindow === null
                  ? undefined
                  : toLocalDateTime(
                      new Date(availabilityWindow.availableStart),
                    )
              }
              max={
                availabilityWindow === null
                  ? undefined
                  : toLocalDateTime(new Date(availabilityWindow.availableEnd))
              }
              onChange={(event) => setEnd(event.target.value)}
              disabled={availabilityWindow === null}
              required
            />
          </label>
          <label>
            Starting cash
            <span className="input-prefix">
              <span>$</span>
              <input
                type="number"
                min={1}
                max={100_000_000}
                step={1}
                value={initialCapitalDollars}
                onChange={(event) =>
                  setInitialCapitalDollars(event.currentTarget.valueAsNumber)
                }
                required
              />
            </span>
          </label>
          <button
            className="primary wide"
            disabled={running || availabilityWindow === null}
            type="submit"
          >
            {running ? "Replaying L2 events…" : "Run backtest"}
          </button>
          <p className="backtest-run-note">
            Interactive runs cover up to 31 days and 500,000 causally observed
            events. Coverage gaps fail closed instead of inventing liquidity.
          </p>
        </aside>
      </form>
      </fieldset>

      {result !== null && <BacktestResults result={result} />}
    </section>
  );
}

/**
 * Displays the backend-derived replay range beside the time chooser.
 *
 * @param props Coverage loading, error, and usable window.
 * @returns Availability summary.
 */
function BacktestCoverageSummary(props: {
  /** Whether the coverage request is pending. */
  coverageLoading: boolean;
  /** Safe coverage request error. */
  error: string | null;
  /** Minute-aligned selectable window. */
  window: BacktestAvailabilityWindow | null;
  /** Continuous production capture or intentional sampled debug windows. */
  collectionMode: "continuous" | "sampled";
  /** Next scheduled collector start in sampled mode. */
  scheduledNextCaptureAt: string | null;
}): React.JSX.Element {
  if (props.coverageLoading) {
    return (
      <div className="backtest-coverage" role="status">
        <strong>Checking stored L2 coverage…</strong>
      </div>
    );
  }
  if (props.error !== null) {
    return (
      <div className="backtest-coverage unavailable" role="alert">
        <strong>Stored L2 coverage is unavailable</strong>
        <span>{props.error}</span>
      </div>
    );
  }
  if (props.window === null) {
    return (
      <div className="backtest-coverage unavailable">
        <strong>No replayable L2 window</strong>
        <span>The selected venue has no complete minute to backtest yet.</span>
      </div>
    );
  }
  return (
    <div className="backtest-coverage">
      <strong>
        {props.collectionMode === "sampled"
          ? "Sampled L2 available"
          : "Replayable L2 available"}
      </strong>
      <span>
        {formatDateTime(props.window.availableStart)} –{" "}
        {formatDateTime(props.window.availableEnd)}
      </span>
      <small>
        {props.window.marketCount.toLocaleString("en-US")} markets ·{" "}
        {props.window.quality}
        {props.window.gapCount > 0
          ? ` · ${props.window.gapCount.toLocaleString("en-US")} known gaps`
          : ""}
        {" · "}updated {formatDateTime(props.window.freshestObservedAt)}
        {props.collectionMode === "sampled" &&
        props.scheduledNextCaptureAt !== null
          ? ` · next capture ${formatDateTime(props.scheduledNextCaptureAt)}`
          : ""}
      </small>
    </div>
  );
}

/**
 * Renders provider token and cost accounting.
 *
 * @param props Provider usage.
 * @returns Compact usage badges.
 */
function UsageSummary(props: {
  /** Provider usage. */
  usage: StrategyGenerationUsage;
}): React.JSX.Element {
  const tokenCount = props.usage.promptTokens + props.usage.completionTokens;
  return (
    <div className="ai-usage" aria-label="AI generation usage">
      <span>{props.usage.model}</span>
      <span>{tokenCount.toLocaleString("en-US")} tokens</span>
      <span>{formatMoney(props.usage.costDollars, 4)}</span>
    </div>
  );
}

/**
 * Edits the strategy's descriptive identity.
 *
 * @param props Strategy and update callback.
 * @returns Identity card.
 */
function StrategyEditor(props: StrategyEditorProps): React.JSX.Element {
  return (
    <FormCard number="01" title="Strategy">
      <div className="form-grid two">
        <label>
          Name
          <input
            value={props.strategy.name}
            maxLength={120}
            onChange={(event) =>
              props.onChange({ ...props.strategy, name: event.target.value })
            }
            required
          />
        </label>
        <label className="form-span">
          Description
          <textarea
            rows={2}
            value={props.strategy.description}
            maxLength={2_000}
            onChange={(event) =>
              props.onChange({
                ...props.strategy,
                description: event.target.value,
              })
            }
          />
        </label>
      </div>
    </FormCard>
  );
}

/**
 * Edits recurring venue, asset, and duration families.
 *
 * @param props Strategy and update callback.
 * @returns Universe card.
 */
function UniverseEditor(props: StrategyEditorProps): React.JSX.Element {
  const template =
    props.strategy.universe.families[0] ??
    DEFAULT_BACKTEST_STRATEGY_V2.universe.families[0]!;
  const selected = new Set(
    props.strategy.universe.families.map((family) => family.underlying),
  );

  /**
   * Applies one shared family field.
   *
   * @param update Partial family.
   * @returns Nothing.
   */
  function updateFamilies(update: Partial<StrategyMarketFamily>): void {
    props.onChange({
      ...props.strategy,
      universe: {
        families: props.strategy.universe.families.map((family) => ({
          ...family,
          ...update,
        })),
      },
    });
  }

  /**
   * Toggles one underlying.
   *
   * @param underlying Asset to toggle.
   * @returns Nothing.
   */
  function toggleUnderlying(underlying: StrategyUnderlying): void {
    const families = selected.has(underlying)
      ? props.strategy.universe.families.filter(
          (family) => family.underlying !== underlying,
        )
      : [...props.strategy.universe.families, { ...template, underlying }];
    if (families.length > 0) {
      props.onChange({ ...props.strategy, universe: { families } });
    }
  }

  return (
    <FormCard number="02" title="Market universe">
      <div className="form-grid two">
        <label>
          Venue
          <select
            value={template.venue}
            onChange={(event) =>
              updateFamilies({
                venue: event.target.value as StrategyMarketFamily["venue"],
              })
            }
          >
            <option value="polymarket">Polymarket</option>
            <option value="kalshi">Kalshi</option>
          </select>
        </label>
        <label>
          Rolling duration
          <select
            value={template.durationMinutes}
            onChange={(event) =>
              updateFamilies({
                durationMinutes: Number(
                  event.target.value,
                ) as StrategyMarketFamily["durationMinutes"],
              })
            }
          >
            <option value={5}>5 minutes</option>
            <option value={15}>15 minutes</option>
          </select>
        </label>
        <fieldset className="form-span chip-fieldset">
          <legend>Underlying assets</legend>
          <div className="choice-chips">
            {UNDERLYINGS.map((underlying) => (
              <label
                className={selected.has(underlying) ? "selected" : ""}
                key={underlying}
              >
                <input
                  type="checkbox"
                  checked={selected.has(underlying)}
                  onChange={() => toggleUnderlying(underlying)}
                />
                {underlying}
              </label>
            ))}
          </div>
        </fieldset>
      </div>
    </FormCard>
  );
}

/**
 * Edits relative entry timing and outcome selection.
 *
 * @param props Strategy and update callback.
 * @returns Timing card.
 */
function TimingEditor(props: StrategyEditorProps): React.JSX.Element {
  const entry = props.strategy.entry;
  return (
    <FormCard number="03" title="Entry timing">
      <div className="form-grid two">
        <label>
          Anchor
          <select
            value={entry.anchor}
            onChange={(event) =>
              props.onChange({
                ...props.strategy,
                entry: {
                  ...entry,
                  anchor: event.target.value as typeof entry.anchor,
                },
              })
            }
          >
            <option value="market_open">Market open</option>
            <option value="market_close">Market close</option>
          </select>
        </label>
        <label>
          Outcome
          <select
            value={entry.outcomeSelection}
            onChange={(event) =>
              props.onChange({
                ...props.strategy,
                entry: {
                  ...entry,
                  outcomeSelection:
                    event.target.value as typeof entry.outcomeSelection,
                },
              })
            }
          >
            <option value="leading">Leading outcome</option>
            <option value="up">Up</option>
            <option value="down">Down</option>
            <option value="reference_direction">Spot-price direction</option>
          </select>
        </label>
        <NumberField
          label="Window starts (seconds from anchor)"
          min={-86_400}
          max={86_400}
          value={entry.startOffsetSeconds}
          onChange={(startOffsetSeconds) =>
            props.onChange({
              ...props.strategy,
              entry: { ...entry, startOffsetSeconds },
            })
          }
        />
        <NumberField
          label="Window ends (seconds from anchor)"
          min={-86_400}
          max={86_400}
          value={entry.endOffsetSeconds}
          onChange={(endOffsetSeconds) =>
            props.onChange({
              ...props.strategy,
              entry: { ...entry, endOffsetSeconds },
            })
          }
        />
      </div>
      <p className="field-help">
        Negative offsets occur before the anchor. Replay uses collector
        observation time, never future source timestamps.
      </p>
    </FormCard>
  );
}

/**
 * Edits weighted entry signals and the executable-edge gate.
 *
 * @param props Strategy and update callback.
 * @returns Weighted signal card.
 */
function SignalsEditor(props: StrategyEditorProps): React.JSX.Element {
  const entry = props.strategy.entry;

  /**
   * Replaces all weighted signals.
   *
   * @param signals Next signals.
   * @returns Nothing.
   */
  function updateSignals(signals: StrategyWeightedSignalV2[]): void {
    props.onChange({ ...props.strategy, entry: { ...entry, signals } });
  }

  return (
    <FormCard number="04" title="Weighted signal score">
      <div className="form-grid three score-gates">
        <NumberField
          label="Minimum score (−100 to 100)"
          min={-100}
          max={100}
          step={0.1}
          value={entry.minimumScorePercent100}
          onChange={(minimumScorePercent100) =>
            props.onChange({
              ...props.strategy,
              entry: { ...entry, minimumScorePercent100 },
            })
          }
        />
        <NumberField
          label="Expected move at score 100 (cents)"
          min={0}
          max={100}
          step={0.01}
          value={entry.expectedMoveAtFullScorePercent1 * 100}
          onChange={(value) =>
            props.onChange({
              ...props.strategy,
              entry: {
                ...entry,
                expectedMoveAtFullScorePercent1: value / 100,
              },
            })
          }
        />
        <NumberField
          label="Minimum net edge %"
          min={-100}
          max={10_000}
          step={0.01}
          value={entry.minimumExpectedNetEdgePercent100}
          onChange={(minimumExpectedNetEdgePercent100) =>
            props.onChange({
              ...props.strategy,
              entry: { ...entry, minimumExpectedNetEdgePercent100 },
            })
          }
        />
        <NumberField
          label="Score must persist ms"
          min={0}
          max={60_000}
          step={1}
          value={entry.minimumScorePersistenceMilliseconds ?? 0}
          onChange={(minimumScorePersistenceMilliseconds) =>
            props.onChange({
              ...props.strategy,
              entry: { ...entry, minimumScorePersistenceMilliseconds },
            })
          }
        />
        <label>
          Reference agreement
          <select
            value={entry.referenceAgreement?.source ?? "none"}
            onChange={(event) =>
              props.onChange({
                ...props.strategy,
                entry: {
                  ...entry,
                  referenceAgreement:
                    event.target.value === "none"
                      ? null
                      : {
                          source: event.target.value as
                            | "chainlink"
                            | "binance",
                          lookbackMilliseconds:
                            entry.referenceAgreement
                              ?.lookbackMilliseconds ?? 1_000,
                          minimumMomentumPercent100:
                            entry.referenceAgreement
                              ?.minimumMomentumPercent100 ?? 0.01,
                        },
                },
              })
            }
          >
            <option value="none">No hard gate</option>
            <option value="chainlink">Chainlink agrees</option>
            <option value="binance">Binance agrees</option>
          </select>
        </label>
        {entry.referenceAgreement !== undefined &&
          entry.referenceAgreement !== null && (
            <>
              <label>
                Reference lookback
                <select
                  value={entry.referenceAgreement.lookbackMilliseconds}
                  onChange={(event) =>
                    props.onChange({
                      ...props.strategy,
                      entry: {
                        ...entry,
                        referenceAgreement: {
                          ...entry.referenceAgreement!,
                          lookbackMilliseconds: Number(
                            event.target.value,
                          ) as 250 | 1_000 | 3_000 | 10_000,
                        },
                      },
                    })
                  }
                >
                  <option value={250}>250 ms</option>
                  <option value={1_000}>1 second</option>
                  <option value={3_000}>3 seconds</option>
                  <option value={10_000}>10 seconds</option>
                </select>
              </label>
              <NumberField
                label="Minimum reference move %"
                min={0}
                max={100}
                step={0.001}
                value={
                  entry.referenceAgreement.minimumMomentumPercent100
                }
                onChange={(minimumMomentumPercent100) =>
                  props.onChange({
                    ...props.strategy,
                    entry: {
                      ...entry,
                      referenceAgreement: {
                        ...entry.referenceAgreement!,
                        minimumMomentumPercent100,
                      },
                    },
                  })
                }
              />
            </>
          )}
        <OptionalNumberInput
          label="Maximum book age ms"
          min={1}
          max={60_000}
          step={1}
          value={entry.maximumBookAgeMilliseconds ?? null}
          onChange={(maximumBookAgeMilliseconds) =>
            props.onChange({
              ...props.strategy,
              entry: { ...entry, maximumBookAgeMilliseconds },
            })
          }
        />
        <OptionalNumberInput
          label="Abstain at ephemeral penalty %"
          min={0}
          max={100}
          step={0.1}
          value={
            entry.maximumEphemeralLiquidityPenaltyPercent100 ?? null
          }
          onChange={(maximumEphemeralLiquidityPenaltyPercent100) =>
            props.onChange({
              ...props.strategy,
              entry: {
                ...entry,
                maximumEphemeralLiquidityPenaltyPercent100,
              },
            })
          }
        />
      </div>
      <div className="condition-list weighted-signal-list">
        {entry.signals.map((weighted, index) => (
          <SignalEditor
            key={weighted.id}
            weighted={weighted}
            onChange={(next) =>
              updateSignals(
                entry.signals.map((candidate, candidateIndex) =>
                  candidateIndex === index ? next : candidate,
                ),
              )
            }
            onRemove={() =>
              updateSignals(
                entry.signals.filter(
                  (_candidate, candidateIndex) => candidateIndex !== index,
                ),
              )
            }
          />
        ))}
      </div>
      <button
        className="secondary compact"
        type="button"
        onClick={() =>
          updateSignals([
            ...entry.signals,
            {
              id: `depth-${entry.signals.length + 1}`,
              weight: 1,
              signal: createSignal("depth_imbalance"),
            },
          ])
        }
      >
        Add signal
      </button>
      <p className="field-help">
        Weights are normalized by the engine. Persistence, reference agreement,
        fresh books, and ephemeral-liquidity abstention are hard gates;
        ephemeral liquidity is not proof of intent.
      </p>
    </FormCard>
  );
}

/**
 * Edits one named weighted signal.
 *
 * @param props Weighted signal and callbacks.
 * @returns Responsive signal row.
 */
function SignalEditor(props: {
  /** Current weighted signal. */
  weighted: StrategyWeightedSignalV2;
  /** Replaces the signal. */
  onChange: (signal: StrategyWeightedSignalV2) => void;
  /** Removes the signal. */
  onRemove: () => void;
}): React.JSX.Element {
  const { weighted } = props;
  const signal = weighted.signal;
  return (
    <div className="condition-row weighted-signal-row">
      <label>
        Name
        <input
          value={weighted.id}
          maxLength={80}
          onChange={(event) =>
            props.onChange({ ...weighted, id: event.target.value })
          }
        />
      </label>
      <label>
        Weight
        <input
          type="number"
          min={0}
          max={1_000}
          step={0.05}
          value={weighted.weight}
          onChange={(event) =>
            props.onChange({
              ...weighted,
              weight: event.currentTarget.valueAsNumber,
            })
          }
        />
      </label>
      <label>
        Signal
        <select
          value={signal.type}
          onChange={(event) =>
            props.onChange({
              ...weighted,
              signal: createSignal(
                event.target.value as StrategySignalV2["type"],
              ),
            })
          }
        >
          {SIGNAL_TYPES.map((type) => (
            <option value={type} key={type}>
              {formatSignalType(type)}
            </option>
          ))}
        </select>
      </label>
      {hasLookback(signal) && (
        <label>
          Lookback
          <select
            value={signal.lookbackMilliseconds}
            onChange={(event) =>
              props.onChange({
                ...weighted,
                signal: replaceLookback(
                  signal,
                  Number(event.target.value),
                ),
              })
            }
          >
            {(signal.type === "order_flow_imbalance"
              ? [250, 1_000, 3_000]
              : [250, 1_000, 3_000, 10_000]
            ).map((value) => (
              <option value={value} key={value}>
                {formatDuration(value)}
              </option>
            ))}
          </select>
        </label>
      )}
      {hasDepth(signal) && (
        <>
          <label>
            Depth
            <select
              value={signal.depth.mode}
              onChange={(event) =>
                props.onChange({
                  ...weighted,
                  signal: {
                    ...signal,
                    depth:
                      event.target.value === "levels"
                        ? { mode: "levels", levels: 5 }
                        : {
                            mode: "price_distance",
                            distancePercent100: 2,
                          },
                  },
                })
              }
            >
              <option value="levels">Book levels</option>
              <option value="price_distance">Price band</option>
            </select>
          </label>
          <NumberField
            label={
              signal.depth.mode === "levels" ? "Levels" : "Distance %"
            }
            min={signal.depth.mode === "levels" ? 1 : 0.01}
            max={signal.depth.mode === "levels" ? 100 : 100}
            step={signal.depth.mode === "levels" ? 1 : 0.01}
            value={
              signal.depth.mode === "levels"
                ? signal.depth.levels
                : signal.depth.distancePercent100
            }
            onChange={(value) =>
              props.onChange({
                ...weighted,
                signal: {
                  ...signal,
                  depth:
                    signal.depth.mode === "levels"
                      ? { mode: "levels", levels: value }
                      : {
                          mode: "price_distance",
                          distancePercent100: value,
                        },
                },
              })
            }
          />
        </>
      )}
      {signal.type === "ephemeral_liquidity_score" && (
        <NumberField
          label="Maximum resting ms"
          min={1}
          max={signal.lookbackMilliseconds}
          step={1}
          value={signal.maximumRestingMilliseconds}
          onChange={(maximumRestingMilliseconds) =>
            props.onChange({
              ...weighted,
              signal: { ...signal, maximumRestingMilliseconds },
            })
          }
        />
      )}
      {(signal.type === "reference_momentum" ||
        signal.type === "reference_target_distance" ||
        signal.type === "reference_direction") && (
        <label>
          Reference
          <select
            value={signal.source}
            onChange={(event) =>
              props.onChange({
                ...weighted,
                signal: {
                  ...signal,
                  source: event.target.value as typeof signal.source,
                },
              })
            }
          >
            <option value="chainlink">Chainlink</option>
            <option value="binance">Binance</option>
          </select>
        </label>
      )}
      {signal.type === "reference_target_distance" && (
        <label>
          Normalization
          <select
            value={signal.normalizationLookbackMilliseconds}
            onChange={(event) =>
              props.onChange({
                ...weighted,
                signal: {
                  ...signal,
                  normalizationLookbackMilliseconds: Number(
                    event.target.value,
                  ) as typeof signal.normalizationLookbackMilliseconds,
                },
              })
            }
          >
            {[1_000, 3_000, 10_000, 60_000].map((value) => (
              <option value={value} key={value}>
                {formatDuration(value)}
              </option>
            ))}
          </select>
        </label>
      )}
      {signal.type === "price" && (
        <>
          <label>
            Quote
            <select
              value={signal.field}
              onChange={(event) =>
                props.onChange({
                  ...weighted,
                  signal: {
                    ...signal,
                    field: event.target.value as typeof signal.field,
                  },
                })
              }
            >
              <option value="bid">Bid</option>
              <option value="ask">Ask</option>
              <option value="midpoint">Midpoint diagnostic</option>
            </select>
          </label>
          <ComparatorField
            operator={signal.operator}
            onChange={(operator) =>
              props.onChange({
                ...weighted,
                signal: { ...signal, operator },
              })
            }
          />
          <NumberField
            label="Threshold cents"
            min={0}
            max={100}
            step={0.1}
            value={signal.valuePercent1 * 100}
            onChange={(value) =>
              props.onChange({
                ...weighted,
                signal: { ...signal, valuePercent1: value / 100 },
              })
            }
          />
        </>
      )}
      {signal.type === "trend" && (
        <>
          <NumberField
            label="Lookback seconds"
            min={1}
            max={86_400}
            step={1}
            value={signal.lookbackSeconds}
            onChange={(lookbackSeconds) =>
              props.onChange({
                ...weighted,
                signal: { ...signal, lookbackSeconds },
              })
            }
          />
          <ComparatorField
            operator={signal.operator}
            onChange={(operator) =>
              props.onChange({
                ...weighted,
                signal: { ...signal, operator },
              })
            }
          />
          <NumberField
            label="Return %"
            min={-10_000}
            max={10_000}
            step={0.01}
            value={signal.returnPercent100}
            onChange={(returnPercent100) =>
              props.onChange({
                ...weighted,
                signal: { ...signal, returnPercent100 },
              })
            }
          />
        </>
      )}
      {signal.type === "book_imbalance" && (
        <>
          <ComparatorField
            operator={signal.operator}
            onChange={(operator) =>
              props.onChange({
                ...weighted,
                signal: { ...signal, operator },
              })
            }
          />
          <NumberField
            label="Imbalance %"
            min={-100}
            max={100}
            step={0.1}
            value={signal.imbalancePercent100}
            onChange={(imbalancePercent100) =>
              props.onChange({
                ...weighted,
                signal: { ...signal, imbalancePercent100 },
              })
            }
          />
        </>
      )}
      {signal.type === "reference_direction" && (
        <NumberField
          label="Minimum move %"
          min={0}
          max={100}
          step={0.01}
          value={signal.minimumMovePercent100}
          onChange={(minimumMovePercent100) =>
            props.onChange({
              ...weighted,
              signal: { ...signal, minimumMovePercent100 },
            })
          }
        />
      )}
      <button
        aria-label="Remove signal"
        className="condition-remove"
        onClick={props.onRemove}
        type="button"
      >
        ×
      </button>
    </div>
  );
}

/**
 * Edits entry order, L2 execution assumptions, and portfolio limits.
 *
 * @param props Strategy and update callback.
 * @returns Execution card.
 */
function ExecutionEditor(props: StrategyEditorProps): React.JSX.Element {
  const { entry, execution, portfolio } = props.strategy;
  return (
    <FormCard number="05" title="Execution and sizing">
      <div className="form-grid three">
        <label>
          Order
          <select
            value={entry.order.type}
            onChange={(event) => {
              const type = event.target.value as typeof entry.order.type;
              props.onChange({
                ...props.strategy,
                entry: {
                  ...entry,
                  order: {
                    ...entry.order,
                    type,
                    limitPricePercent1:
                      type === "limit"
                        ? (entry.order.limitPricePercent1 ?? 0.5)
                        : null,
                  },
                },
              });
            }}
          >
            <option value="market">Market</option>
            <option value="limit">Limit</option>
          </select>
        </label>
        <label>
          Size by
          <select
            value={entry.order.sizing}
            onChange={(event) =>
              props.onChange({
                ...props.strategy,
                entry: {
                  ...entry,
                  order: {
                    ...entry.order,
                    sizing: event.target.value as typeof entry.order.sizing,
                  },
                },
              })
            }
          >
            <option value="fixed_dollars">All-in dollars</option>
            <option value="fixed_contracts">Contracts</option>
          </select>
        </label>
        <NumberField
          label="Size"
          min={0.01}
          step={0.01}
          value={entry.order.size}
          onChange={(size) =>
            props.onChange({
              ...props.strategy,
              entry: { ...entry, order: { ...entry.order, size } },
            })
          }
        />
        {entry.order.type === "limit" && (
          <NumberField
            label="Worst entry cents"
            min={0}
            max={100}
            step={0.1}
            value={(entry.order.limitPricePercent1 ?? 0) * 100}
            onChange={(value) =>
              props.onChange({
                ...props.strategy,
                entry: {
                  ...entry,
                  order: {
                    ...entry.order,
                    limitPricePercent1: value / 100,
                  },
                },
              })
            }
          />
        )}
        <NumberField
          label="Latency ms"
          min={0}
          max={60_000}
          step={1}
          value={execution.latencyMilliseconds}
          onChange={(latencyMilliseconds) =>
            props.onChange({
              ...props.strategy,
              execution: { ...execution, latencyMilliseconds },
            })
          }
        />
        <NumberField
          label="Displayed depth participation %"
          min={0.01}
          max={100}
          step={0.01}
          value={execution.displayedDepthParticipationPercent100}
          onChange={(displayedDepthParticipationPercent100) =>
            props.onChange({
              ...props.strategy,
              execution: {
                ...execution,
                displayedDepthParticipationPercent100,
              },
            })
          }
        />
        <OptionalNumberInput
          label="Maximum slippage %"
          min={0}
          max={10_000}
          value={execution.maximumSlippagePercent100}
          onChange={(maximumSlippagePercent100) =>
            props.onChange({
              ...props.strategy,
              execution: { ...execution, maximumSlippagePercent100 },
            })
          }
        />
        <NumberField
          label="Expected-edge buffer %"
          min={0}
          max={100}
          step={0.01}
          value={execution.expectedEdgeSlippageBufferPercent100 ?? 0}
          onChange={(expectedEdgeSlippageBufferPercent100) =>
            props.onChange({
              ...props.strategy,
              execution: {
                ...execution,
                expectedEdgeSlippageBufferPercent100,
              },
            })
          }
        />
        <NumberField
          label="Entries per market"
          min={1}
          max={20}
          step={1}
          value={entry.maxEntriesPerMarket}
          onChange={(maxEntriesPerMarket) =>
            props.onChange({
              ...props.strategy,
              entry: { ...entry, maxEntriesPerMarket },
            })
          }
        />
        <NumberField
          label="Max open positions"
          min={1}
          max={1_000}
          step={1}
          value={portfolio.maxConcurrentPositions}
          onChange={(maxConcurrentPositions) =>
            props.onChange({
              ...props.strategy,
              portfolio: { ...portfolio, maxConcurrentPositions },
            })
          }
        />
        <NumberField
          label="Max dollars per position"
          min={0.01}
          step={0.01}
          value={portfolio.maxPositionDollars}
          onChange={(maxPositionDollars) =>
            props.onChange({
              ...props.strategy,
              portfolio: { ...portfolio, maxPositionDollars },
            })
          }
        />
      </div>
      <p className="field-help">
        Entry walks asks and exit walks bids after latency, consuming no more
        than the configured share of each displayed level.
      </p>
    </FormCard>
  );
}

/**
 * Edits executable-price and score-based exit rules.
 *
 * @param props Strategy and update callback.
 * @returns Exit card.
 */
function ExitEditor(props: StrategyEditorProps): React.JSX.Element {
  const exit = props.strategy.exit;

  /**
   * Updates exit fields.
   *
   * @param update Partial exit definition.
   * @returns Nothing.
   */
  function updateExit(update: Partial<typeof exit>): void {
    props.onChange({ ...props.strategy, exit: { ...exit, ...update } });
  }

  return (
    <FormCard number="06" title="Dynamic exits">
      <div className="form-grid three">
        <OptionalNumberInput
          label="Executable-bid take profit %"
          value={exit.takeProfitPercent100}
          onChange={(takeProfitPercent100) =>
            updateExit({ takeProfitPercent100 })
          }
        />
        <OptionalNumberInput
          label="Executable-bid stop loss %"
          value={exit.stopLossPercent100}
          onChange={(stopLossPercent100) => updateExit({ stopLossPercent100 })}
        />
        <OptionalNumberInput
          label="Maximum holding seconds"
          value={exit.maximumHoldingSeconds}
          onChange={(maximumHoldingSeconds) =>
            updateExit({ maximumHoldingSeconds })
          }
        />
        <OptionalSignedNumberInput
          label="Signal reversal score"
          value={exit.signalReversalScorePercent100}
          onChange={(signalReversalScorePercent100) =>
            updateExit({ signalReversalScorePercent100 })
          }
        />
        <OptionalSignedNumberInput
          label="Pressure decay score"
          value={exit.pressureDecayScorePercent100}
          onChange={(pressureDecayScorePercent100) =>
            updateExit({ pressureDecayScorePercent100 })
          }
        />
        <NumberField
          label="Exit before close (seconds)"
          min={0}
          max={86_400}
          step={1}
          value={exit.exitBeforeCloseSeconds}
          onChange={(exitBeforeCloseSeconds) =>
            updateExit({ exitBeforeCloseSeconds })
          }
        />
      </div>
    </FormCard>
  );
}

/**
 * Renders a reusable numeric input.
 *
 * @param props Numeric field properties.
 * @returns Number input.
 */
function NumberField(props: {
  /** Visible label. */
  label: string;
  /** Current number. */
  value: number;
  /** Optional minimum. */
  min?: number;
  /** Optional maximum. */
  max?: number;
  /** Optional increment. */
  step?: number;
  /** Update callback. */
  onChange: (value: number) => void;
}): React.JSX.Element {
  return (
    <label>
      {props.label}
      <input
        type="number"
        min={props.min}
        max={props.max}
        step={props.step}
        value={props.value}
        onChange={(event) => props.onChange(event.currentTarget.valueAsNumber)}
        required
      />
    </label>
  );
}

/**
 * Renders a nullable positive numeric rule.
 *
 * @param props Nullable number properties.
 * @returns Nullable number input.
 */
function OptionalNumberInput(props: {
  /** Visible label. */
  label: string;
  /** Nullable number. */
  value: number | null;
  /** Minimum permitted value. */
  min?: number;
  /** Maximum permitted value. */
  max?: number;
  /** Input step. */
  step?: number;
  /** Update callback. */
  onChange: (value: number | null) => void;
}): React.JSX.Element {
  return (
    <label>
      {props.label}
      <input
        type="number"
        min={props.min ?? 0.01}
        max={props.max}
        step={props.step ?? 0.01}
        placeholder="No rule"
        value={props.value ?? ""}
        onChange={(event) =>
          props.onChange(
            event.target.value === ""
              ? null
              : event.currentTarget.valueAsNumber,
          )
        }
      />
    </label>
  );
}

/**
 * Renders a nullable signed score input.
 *
 * @param props Nullable signed number properties.
 * @returns Nullable signed number input.
 */
function OptionalSignedNumberInput(props: {
  /** Visible label. */
  label: string;
  /** Nullable number. */
  value: number | null;
  /** Update callback. */
  onChange: (value: number | null) => void;
}): React.JSX.Element {
  return (
    <label>
      {props.label}
      <input
        type="number"
        min={-100}
        max={100}
        step={0.1}
        placeholder="No rule"
        value={props.value ?? ""}
        onChange={(event) =>
          props.onChange(
            event.target.value === ""
              ? null
              : event.currentTarget.valueAsNumber,
          )
        }
      />
    </label>
  );
}

/**
 * Renders a numeric comparator used by legacy signals retained in V2.
 *
 * @param props Current comparator and update callback.
 * @returns Comparator select.
 */
function ComparatorField(props: {
  /** Current comparator. */
  operator: "gt" | "gte" | "lt" | "lte";
  /** Update callback. */
  onChange: (operator: "gt" | "gte" | "lt" | "lte") => void;
}): React.JSX.Element {
  return (
    <label>
      Comparison
      <select
        value={props.operator}
        onChange={(event) =>
          props.onChange(
            event.target.value as "gt" | "gte" | "lt" | "lte",
          )
        }
      >
        <option value="gt">greater than</option>
        <option value="gte">at least</option>
        <option value="lt">less than</option>
        <option value="lte">at most</option>
      </select>
    </label>
  );
}

/**
 * Provides consistent numbered form-section chrome.
 *
 * @param props Section number, title, and fields.
 * @returns Styled form card.
 */
function FormCard(props: {
  /** Visible step number. */
  number: string;
  /** Visible title. */
  title: string;
  /** Form controls. */
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <section className="surface strategy-form-card">
      <header>
        <span>{props.number}</span>
        <h2>{props.title}</h2>
      </header>
      {props.children}
    </section>
  );
}

/**
 * Renders metrics, decisions, equity, warnings, and completed trades.
 *
 * @param props Completed result.
 * @returns Result surface.
 */
function BacktestResults(props: { result: BacktestResult }): React.JSX.Element {
  const { result } = props;
  const metrics = result.metrics;
  return (
    <section className="backtest-results" aria-live="polite">
      <div className="backtest-results-heading">
        <div>
          <p className="eyebrow">Completed run</p>
          <h2>Backtest results</h2>
        </div>
        <span>
          {formatDateTime(result.start)} – {formatDateTime(result.end)}
        </span>
      </div>
      <div className="backtest-metrics">
        <ResultMetric
          label="Net profit"
          value={formatSignedMoney(metrics.netProfitDollars)}
          tone={metrics.netProfitDollars >= 0 ? "positive" : "negative"}
        />
        <ResultMetric
          label="Return"
          value={formatPercent(metrics.returnPercent100)}
          tone={metrics.returnPercent100 >= 0 ? "positive" : "negative"}
        />
        <ResultMetric
          label="Final equity"
          value={formatMoney(metrics.finalEquityDollars)}
        />
        <ResultMetric
          label="Max drawdown"
          value={formatPercent(-metrics.maxDrawdownPercent100)}
          tone="negative"
        />
        <ResultMetric label="Trades" value={`${metrics.tradeCount}`} />
        <ResultMetric
          label="Win rate"
          value={formatPercent(metrics.winRatePercent100)}
        />
        <ResultMetric
          label="Profit factor"
          value={
            metrics.profitFactor === null
              ? "—"
              : metrics.profitFactor.toFixed(2)
          }
        />
        <ResultMetric label="Fees" value={formatMoney(metrics.totalFeesDollars)} />
      </div>
      <EquityCurve result={result} />
      <DecisionSummary decisions={result.decisions ?? []} />
      <div className="surface trade-stats">
        <h3>Trade statistics</h3>
        <dl>
          <div><dt>Winning trades</dt><dd>{metrics.winningTradeCount}</dd></div>
          <div><dt>Losing trades</dt><dd>{metrics.losingTradeCount}</dd></div>
          <div><dt>Average trade</dt><dd>{formatSignedMoney(metrics.averageTradeProfitDollars)}</dd></div>
          <div><dt>Average win</dt><dd>{formatSignedMoney(metrics.averageWinDollars)}</dd></div>
          <div><dt>Average loss</dt><dd>{formatSignedMoney(metrics.averageLossDollars)}</dd></div>
          <div><dt>Largest win</dt><dd>{formatSignedMoney(metrics.largestWinDollars)}</dd></div>
          <div><dt>Largest loss</dt><dd>{formatSignedMoney(metrics.largestLossDollars)}</dd></div>
          <div><dt>Rejected signals</dt><dd>{metrics.rejectedSignalCount}</dd></div>
        </dl>
      </div>
      {result.warnings.length > 0 && (
        <details className="surface backtest-warnings">
          <summary>Methodology and data notes ({result.warnings.length})</summary>
          <ul>
            {result.warnings.map((warning) => <li key={warning}>{warning}</li>)}
          </ul>
        </details>
      )}
      <TradeTable trades={result.trades} />
    </section>
  );
}

/**
 * Renders a compact distribution of retained decision outcomes.
 *
 * @param props Retained decisions.
 * @returns Decision diagnostics summary.
 */
function DecisionSummary(props: {
  /** Retained decisions. */
  decisions: BacktestDecisionDiagnostic[];
}): React.JSX.Element {
  if (props.decisions.length === 0) {
    return (
      <div className="surface decision-summary empty">
        <h3>No evaluable decisions</h3>
        <p>
          The replay found no market event inside the strategy window with a
          selectable outcome. Check market-data coverage, the family and date
          range, and the relative open/close offsets.
        </p>
      </div>
    );
  }
  const rejected = props.decisions.filter((decision) => !decision.accepted);
  const reasons = new Map<string, number>();
  for (const decision of rejected) {
    const reason = decision.rejectionReason ?? "unknown";
    reasons.set(reason, (reasons.get(reason) ?? 0) + 1);
  }
  return (
    <details className="surface decision-summary">
      <summary>
        Decision diagnostics ({props.decisions.length.toLocaleString("en-US")}{" "}
        retained)
      </summary>
      <div className="decision-reasons">
        {[...reasons.entries()]
          .sort((left, right) => right[1] - left[1])
          .map(([reason, count]) => (
            <span key={reason}>
              {formatLabel(reason)} <strong>{count}</strong>
            </span>
          ))}
      </div>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Observed</th>
              <th>Phase</th>
              <th>Score</th>
              <th>Expected edge</th>
              <th>Executable</th>
              <th>Decision</th>
            </tr>
          </thead>
          <tbody>
            {props.decisions.slice(-200).map((decision, index) => (
              <tr key={`${decision.at}-${decision.marketId}-${index}`}>
                <td>{formatDateTime(decision.at)}</td>
                <td>{decision.phase}</td>
                <td>{formatPercent(decision.combinedScorePercent100)}</td>
                <td>
                  {decision.expectedNetEdgePercent100 === null
                    ? "—"
                    : formatPercent(decision.expectedNetEdgePercent100)}
                </td>
                <td>
                  {decision.book.executablePricePercent1 === null
                    ? "—"
                    : `${formatCents(decision.book.executablePricePercent1)} × ${decision.book.executableQuantity.toFixed(2)}`}
                </td>
                <td>
                  {decision.accepted
                    ? "Filled"
                    : formatLabel(decision.rejectionReason ?? "rejected")}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  );
}

/**
 * Renders one result metric.
 *
 * @param props Label, formatted value, and optional tone.
 * @returns Metric card.
 */
function ResultMetric(props: {
  /** Metric label. */
  label: string;
  /** Formatted value. */
  value: string;
  /** Optional semantic color. */
  tone?: "positive" | "negative";
}): React.JSX.Element {
  return (
    <article className="surface result-metric">
      <span>{props.label}</span>
      <strong className={props.tone}>{props.value}</strong>
    </article>
  );
}

/**
 * Renders a compact dependency-free SVG equity curve.
 *
 * @param props Completed result.
 * @returns Equity chart.
 */
function EquityCurve(props: { result: BacktestResult }): React.JSX.Element {
  const points = props.result.equityCurve;
  const values = points.map((point) => point.equityDollars);
  const minimum = Math.min(...values);
  const maximum = Math.max(...values);
  const spread = Math.max(maximum - minimum, 0.01);
  const coordinates = points.map((point, index) => ({
    x: points.length <= 1 ? 0 : (index / (points.length - 1)) * 1_000,
    y: 240 - ((point.equityDollars - minimum) / spread) * 220,
  }));
  const positive =
    props.result.metrics.finalEquityDollars >=
    props.result.metrics.initialCapitalDollars;
  return (
    <section className="surface backtest-equity-card">
      <div>
        <div>
          <span>Equity curve</span>
          <strong>{formatMoney(props.result.metrics.finalEquityDollars)}</strong>
        </div>
        <span>
          {formatMoney(minimum)} low · {formatMoney(maximum)} high
        </span>
      </div>
      <div className="backtest-equity-plot">
        <svg
          aria-label="Backtest equity over time"
          preserveAspectRatio="none"
          role="img"
          viewBox="0 0 1000 260"
        >
          {[20, 130, 240].map((y) => (
            <line
              className="equity-grid-line"
              x1="0"
              x2="1000"
              y1={y}
              y2={y}
              key={y}
            />
          ))}
          <polyline
            className={`backtest-equity-line ${positive ? "positive" : "negative"}`}
            points={coordinates
              .map((point) => `${point.x},${point.y}`)
              .join(" ")}
          />
        </svg>
      </div>
      <div className="equity-chart-dates">
        <span>{formatDateTime(points[0]?.at ?? props.result.start)}</span>
        <span>{formatDateTime(points.at(-1)?.at ?? props.result.end)}</span>
      </div>
    </section>
  );
}

/**
 * Renders completed trades with sortable columns and L2 diagnostics.
 *
 * @param props Completed trades.
 * @returns Sortable table.
 */
function TradeTable(props: { trades: BacktestTrade[] }): React.JSX.Element {
  const [sort, setSort] = useState<TradeSort>({
    key: "entryAt",
    direction: "asc",
  });
  const trades = useMemo(
    () =>
      [...props.trades].sort((left, right) =>
        compareTrades(left, right, sort),
      ),
    [props.trades, sort],
  );

  /**
   * Selects or reverses one sort field.
   *
   * @param key Sort field.
   * @returns Nothing.
   */
  function changeSort(key: TradeSortKey): void {
    setSort((current) => ({
      key,
      direction:
        current.key === key && current.direction === "asc" ? "desc" : "asc",
    }));
  }

  return (
    <section className="surface backtest-trades">
      <div>
        <div>
          <h3>Trades</h3>
          <span>{props.trades.length.toLocaleString("en-US")} completed</span>
        </div>
      </div>
      {trades.length === 0 ? (
        <div className="empty-state">
          No entry signal received an executable fill in this range.
        </div>
      ) : (
        <div className="table-wrap table-scroll-pane">
          <table>
            <thead>
              <tr>
                <SortableHeader label="Entry" field="entryAt" sort={sort} onSort={changeSort} />
                <SortableHeader label="Market" field="marketTitle" sort={sort} onSort={changeSort} />
                <SortableHeader label="Outcome" field="outcomeName" sort={sort} onSort={changeSort} />
                <SortableHeader label="Entry px" field="entryPricePercent1" sort={sort} onSort={changeSort} />
                <SortableHeader label="Exit px" field="exitPricePercent1" sort={sort} onSort={changeSort} />
                <SortableHeader label="Quantity" field="quantity" sort={sort} onSort={changeSort} />
                <SortableHeader label="P&L" field="profitDollars" sort={sort} onSort={changeSort} />
                <SortableHeader label="Return" field="returnPercent100" sort={sort} onSort={changeSort} />
                <SortableHeader label="Exit" field="exitReason" sort={sort} onSort={changeSort} />
                <th>Diagnostics</th>
              </tr>
            </thead>
            <tbody>
              {trades.map((trade) => (
                <tr key={trade.id}>
                  <td>{formatDateTime(trade.entryAt)}<small>to {formatDateTime(trade.exitAt)}</small></td>
                  <td className="trade-market-cell">{trade.marketTitle}<small>{trade.venue}</small></td>
                  <td>{trade.outcomeName}</td>
                  <td>{formatCents(trade.entryPricePercent1)}</td>
                  <td>{formatCents(trade.exitPricePercent1)}</td>
                  <td>{trade.quantity.toFixed(2)}</td>
                  <td className={trade.profitDollars >= 0 ? "positive" : "negative"}>{formatSignedMoney(trade.profitDollars)}</td>
                  <td className={trade.returnPercent100 >= 0 ? "positive" : "negative"}>{formatPercent(trade.returnPercent100)}</td>
                  <td>{formatLabel(trade.exitReason)}<small>{formatMoney(trade.feesDollars)} fees</small></td>
                  <td><TradeDiagnostics trade={trade} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/**
 * Renders retained diagnostics for one completed V2 trade.
 *
 * @param props Completed trade.
 * @returns Expandable diagnostic detail.
 */
function TradeDiagnostics(props: {
  /** Completed trade. */
  trade: BacktestTrade;
}): React.JSX.Element {
  const diagnostics = props.trade.diagnostics;
  if (diagnostics === undefined) return <span>V1</span>;
  return (
    <details className="trade-diagnostics">
      <summary>Inspect</summary>
      <div className="trade-diagnostic-panel">
        <strong>
          Entry score{" "}
          {formatPercent(diagnostics.entry.combinedScorePercent100)}
        </strong>
        <span>
          Expected net edge{" "}
          {diagnostics.entry.expectedNetEdgePercent100 === null
            ? "—"
            : formatPercent(
                diagnostics.entry.expectedNetEdgePercent100,
              )}
        </span>
        <span>
          Book {diagnostics.entry.book.levelsUsed} levels ·{" "}
          {diagnostics.entry.book.executableQuantity.toFixed(2)} contracts
        </span>
        <span>
          Flow adds B {diagnostics.entry.flow.bidAdds.toFixed(1)} / A{" "}
          {diagnostics.entry.flow.askAdds.toFixed(1)} · cancels B{" "}
          {diagnostics.entry.flow.bidCancels.toFixed(1)} / A{" "}
          {diagnostics.entry.flow.askCancels.toFixed(1)}
        </span>
        <span>
          Aggressive B {diagnostics.entry.flow.aggressiveBuys.toFixed(1)} / S{" "}
          {diagnostics.entry.flow.aggressiveSells.toFixed(1)}
        </span>
        <span>
          Ephemeral penalty{" "}
          {formatPercent(
            diagnostics.entry.ephemeralLiquidityPenaltyPercent100,
          )}
        </span>
        <div className="diagnostic-components">
          {diagnostics.entry.components.map((component) => (
            <span key={component.id}>
              {component.id}: {formatPercent(component.scorePercent100)} ×{" "}
              {component.weight.toFixed(2)}
            </span>
          ))}
        </div>
        <div className="diagnostic-markouts">
          {diagnostics.markouts.map((markout) => (
            <span key={markout.horizonMilliseconds}>
              {formatDuration(markout.horizonMilliseconds)}{" "}
              {markout.netReturnPercent100 === null
                ? "—"
                : formatPercent(markout.netReturnPercent100)}
            </span>
          ))}
        </div>
      </div>
    </details>
  );
}

/**
 * Renders a clickable trade-table heading.
 *
 * @param props Label, field, ordering, and callback.
 * @returns Sortable heading.
 */
function SortableHeader(props: {
  /** Visible label. */
  label: string;
  /** Field controlled by the heading. */
  field: TradeSortKey;
  /** Current ordering. */
  sort: TradeSort;
  /** Sort selection callback. */
  onSort: (key: TradeSortKey) => void;
}): React.JSX.Element {
  const active = props.sort.key === props.field;
  return (
    <th>
      <button
        className={`sort-header ${active ? "active" : ""}`}
        onClick={() => props.onSort(props.field)}
        type="button"
      >
        {props.label}
        {active && (
          <span className="sort-indicator">
            {props.sort.direction === "asc" ? "↑" : "↓"}
          </span>
        )}
      </button>
    </th>
  );
}

/**
 * Creates one complete signal from a selected discriminator.
 *
 * @param type Signal discriminator.
 * @returns Complete signal defaults.
 */
function createSignal(type: StrategySignalV2["type"]): StrategySignalV2 {
  const depth = { mode: "levels" as const, levels: 5 };
  switch (type) {
    case "depth_imbalance":
      return { type, depth };
    case "order_flow_imbalance":
      return { type, lookbackMilliseconds: 1_000, depth };
    case "aggressive_trade_imbalance":
      return { type, lookbackMilliseconds: 1_000 };
    case "microprice_deviation":
      return { type };
    case "liquidity_persistence":
    case "book_resilience":
      return { type, lookbackMilliseconds: 3_000, depth };
    case "ephemeral_liquidity_score":
      return {
        type,
        lookbackMilliseconds: 3_000,
        maximumRestingMilliseconds: 1_000,
        depth,
      };
    case "reference_momentum":
      return { type, source: "chainlink", lookbackMilliseconds: 3_000 };
    case "reference_target_distance":
      return {
        type,
        source: "chainlink",
        normalizationLookbackMilliseconds: 10_000,
      };
    case "price":
      return {
        type,
        field: "ask",
        operator: "gte",
        valuePercent1: 0.5,
      };
    case "trend":
      return {
        type,
        lookbackSeconds: 60,
        operator: "gt",
        returnPercent100: 0,
      };
    case "book_imbalance":
      return { type, operator: "gt", imbalancePercent100: 25 };
    case "reference_direction":
      return { type, source: "chainlink", minimumMovePercent100: 0 };
  }
}

/**
 * Determines whether a signal uses the common millisecond lookback field.
 *
 * @param signal Signal to inspect.
 * @returns Whether the lookback field exists.
 */
function hasLookback(
  signal: StrategySignalV2,
): signal is Extract<
  StrategySignalV2,
  {
    lookbackMilliseconds: 250 | 1_000 | 3_000 | 10_000;
  }
> {
  return "lookbackMilliseconds" in signal;
}

/**
 * Determines whether a signal uses a depth scope.
 *
 * @param signal Signal to inspect.
 * @returns Whether the depth field exists.
 */
function hasDepth(
  signal: StrategySignalV2,
): signal is Extract<StrategySignalV2, { depth: unknown }> {
  return "depth" in signal;
}

/**
 * Replaces the lookback on a signal while preserving its literal constraints.
 *
 * @param signal Signal with a millisecond lookback.
 * @param value Selected millisecond value.
 * @returns Updated signal.
 */
function replaceLookback(
  signal: Extract<
    StrategySignalV2,
    { lookbackMilliseconds: 250 | 1_000 | 3_000 | 10_000 }
  >,
  value: number,
): StrategySignalV2 {
  if (signal.type === "order_flow_imbalance") {
    return {
      ...signal,
      lookbackMilliseconds: value as 250 | 1_000 | 3_000,
    };
  }
  if (signal.type === "ephemeral_liquidity_score") {
    const lookbackMilliseconds =
      value as 250 | 1_000 | 3_000 | 10_000;
    return {
      ...signal,
      lookbackMilliseconds,
      maximumRestingMilliseconds: Math.min(
        signal.maximumRestingMilliseconds,
        lookbackMilliseconds,
      ),
    };
  }
  return {
    ...signal,
    lookbackMilliseconds: value as 250 | 1_000 | 3_000 | 10_000,
  };
}

/**
 * Compares two trades by the selected typed field.
 *
 * @param left First trade.
 * @param right Second trade.
 * @param sort Ordering.
 * @returns Sort comparison.
 */
function compareTrades(
  left: BacktestTrade,
  right: BacktestTrade,
  sort: TradeSort,
): number {
  const leftValue = left[sort.key];
  const rightValue = right[sort.key];
  const comparison =
    typeof leftValue === "number" && typeof rightValue === "number"
      ? leftValue - rightValue
      : String(leftValue).localeCompare(String(rightValue));
  return sort.direction === "asc" ? comparison : -comparison;
}

/**
 * Formats a date for `datetime-local`.
 *
 * @param date Date value.
 * @returns Local minute-precision value.
 */
function toLocalDateTime(date: Date): string {
  const offsetMilliseconds = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offsetMilliseconds)
    .toISOString()
    .slice(0, 16);
}

/**
 * Formats US-dollar values.
 *
 * @param value Numeric dollars.
 * @param maximumFractionDigits Optional precision.
 * @returns Currency text.
 */
function formatMoney(value: number, maximumFractionDigits: number = 2): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits,
  }).format(value);
}

/**
 * Formats signed US-dollar values.
 *
 * @param value Numeric dollars.
 * @returns Signed currency text.
 */
function formatSignedMoney(value: number): string {
  return `${value >= 0 ? "+" : ""}${formatMoney(value)}`;
}

/**
 * Formats a percentage on scale 100.
 *
 * @param value Percentage.
 * @returns Percentage text.
 */
function formatPercent(value: number): string {
  return `${value >= 0 ? "+" : ""}${value.toFixed(2)}%`;
}

/**
 * Formats a probability on scale 1 as cents.
 *
 * @param value Probability.
 * @returns Cent text.
 */
function formatCents(value: number): string {
  return `${(value * 100).toFixed(2)}¢`;
}

/**
 * Formats an ISO timestamp in the configured operational timezone.
 *
 * @param value ISO timestamp.
 * @returns Display date and time.
 */
function formatDateTime(value: string): string {
  return new Date(value).toLocaleString("en-US", {
    timeZone: "America/New_York",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
  });
}

/**
 * Formats a signal or machine reason for readers.
 *
 * @param value Machine label.
 * @returns Title-like label.
 */
function formatLabel(value: string): string {
  return value.replaceAll("_", " ");
}

/**
 * Formats one signal type.
 *
 * @param type Signal discriminator.
 * @returns Reader-facing label.
 */
function formatSignalType(type: StrategySignalV2["type"]): string {
  return formatLabel(type);
}

/**
 * Formats a millisecond duration.
 *
 * @param milliseconds Millisecond duration.
 * @returns Compact duration.
 */
function formatDuration(milliseconds: number): string {
  return milliseconds < 1_000
    ? `${milliseconds}ms`
    : `${milliseconds / 1_000}s`;
}

/**
 * Extracts a safe user-facing error.
 *
 * @param reason Unknown caught value.
 * @returns Error message.
 */
function readError(reason: unknown): string {
  return reason instanceof Error ? reason.message : "The request failed.";
}
